/**
 * AgencyForm — 발송대행 신청 단일 단계 폼
 *
 * QuoteForm Step 4(연락처 + 동의)와 동일한 입력 UI를 단일 메시지에 노출한다.
 * 발송 채널/예상 발송량/발송 빈도 같은 견적 메타데이터는 받지 않는다 — 영업팀이
 * 직접 컨설팅을 수행하기 위함.
 *
 * 데이터 의존성: `quote-request.json`의 `consent` 블록만 재사용.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import quoteRequestData from '@/data/quote-request.json';
import { Field, PrimaryButton, SecondaryButton, inputClass } from './QuoteForm';
import {
  MAX_LENGTHS,
  describeIssue,
  normalizeInput,
  validateContact,
} from '@/lib/utils/sanitize';
import type { AgencyFormData, QuoteContact } from '@/types/qa';

const CONSENT = (
  quoteRequestData.form as {
    consent: { label: string; summary: string; policy_url: string };
  }
).consent;

interface AgencyFormProps {
  formData: Partial<AgencyFormData>;
  onSubmit: (data: AgencyFormData) => void;
  onCancel: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9\-+\s()]{8,}$/;

export function AgencyForm({ formData, onSubmit, onCancel }: AgencyFormProps) {
  const initial: QuoteContact = formData.contact ?? {
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  };
  const [contact, setContact] = useState<QuoteContact>(initial);
  const [consent, setConsent] = useState<boolean>(formData.consent_agreed ?? false);
  const [showConsentDetail, setShowConsentDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [safetyError, setSafetyError] = useState<string | null>(null);

  const isEmailValid = EMAIL_RE.test(contact.email);
  const isPhoneValid = PHONE_RE.test(contact.phone);
  const canSubmit =
    contact.company.trim().length > 0 &&
    contact.name.trim().length > 0 &&
    isEmailValid &&
    isPhoneValid &&
    consent &&
    !submitting;

  function update<K extends keyof QuoteContact>(key: K, value: QuoteContact[K]) {
    const sanitized =
      typeof value === 'string' ? (normalizeInput(value) as QuoteContact[K]) : value;
    setContact((prev) => ({ ...prev, [key]: sanitized }));
    if (safetyError) setSafetyError(null);
  }

  function handleSubmit() {
    if (!canSubmit) return;
    const issues = validateContact(contact);
    if (issues.length > 0) {
      setSafetyError(issues.map(describeIssue).join('\n'));
      return;
    }
    setSubmitting(true);
    onSubmit({ contact, consent_agreed: consent });
  }

  return (
    <div className="ml-flex ml-flex-col ml-gap-2">
      <div className="ml-flex ml-items-center ml-gap-2 ml-mb-2">
        <span
          className={cn(
            'ml-inline-flex ml-items-center ml-justify-center',
            'ml-w-5 ml-h-5 ml-rounded-full',
            'ml-bg-brand-600 ml-text-white ml-text-cb-xs ml-font-semibold'
          )}
          aria-hidden="true"
        >
          ✉
        </span>
        <span className="ml-text-cb-base ml-font-semibold ml-text-ink">
          발송대행 신청 정보를 입력해 주세요
        </span>
      </div>

      <div className="ml-flex ml-flex-col ml-gap-2">
        <Field label="회사명" required>
          <input
            type="text"
            value={contact.company}
            onChange={(e) => update('company', e.target.value)}
            placeholder="(주)메일링크"
            maxLength={MAX_LENGTHS.company}
            className={inputClass}
          />
        </Field>
        <Field label="담당자명" required>
          <input
            type="text"
            value={contact.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="홍길동"
            maxLength={MAX_LENGTHS.name}
            className={inputClass}
          />
        </Field>
        <Field
          label="이메일"
          required
          error={contact.email && !isEmailValid ? '올바른 이메일 형식이 아닙니다' : undefined}
        >
          <input
            type="email"
            value={contact.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="contact@company.co.kr"
            maxLength={MAX_LENGTHS.email}
            className={inputClass}
          />
        </Field>
        <Field
          label="연락처"
          required
          error={contact.phone && !isPhoneValid ? '올바른 연락처를 입력해 주세요' : undefined}
        >
          <input
            type="tel"
            value={contact.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder="010-1234-5678"
            maxLength={MAX_LENGTHS.phone}
            className={inputClass}
          />
        </Field>
        <Field label="대행 요청 사항">
          <textarea
            value={contact.message ?? ''}
            onChange={(e) => update('message', e.target.value)}
            rows={3}
            placeholder="대행 범위, 발송 일정, 예상 발송량, 채널 등"
            maxLength={MAX_LENGTHS.message}
            className={cn(inputClass, 'ml-resize-none')}
          />
        </Field>
      </div>

      <div
        className={cn(
          'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
          'ml-bg-surface ml-border ml-border-line'
        )}
      >
        <label className="ml-flex ml-items-start ml-gap-2 ml-cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="ml-mt-0.5 ml-flex-shrink-0"
          />
          <span className="ml-text-cb-sm ml-text-ink-secondary">{CONSENT.label}</span>
        </label>
        <button
          type="button"
          onClick={() => setShowConsentDetail((v) => !v)}
          className={cn(
            'ml-mt-1 ml-ml-6 ml-text-cb-xs ml-text-brand-500 ml-cursor-pointer',
            'hover:ml-underline'
          )}
        >
          {showConsentDetail ? '간략히 보기' : '자세히 보기'}
        </button>
        {showConsentDetail && (
          <div className="ml-mt-1 ml-ml-6 ml-text-cb-xs ml-text-mute ml-leading-relaxed">
            {CONSENT.summary}{' '}
            <a
              href={CONSENT.policy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-text-brand-500 hover:ml-underline"
            >
              개인정보처리방침 ↗
            </a>
          </div>
        )}
      </div>

      {safetyError && (
        <div
          role="alert"
          className={cn(
            'ml-mt-1 ml-p-2.5 ml-rounded-cb-sm',
            'ml-bg-surface ml-border ml-border-danger',
            'ml-text-cb-sm ml-text-danger ml-whitespace-pre-line'
          )}
        >
          {safetyError}
        </div>
      )}

      <div className="ml-flex ml-gap-2 ml-mt-2">
        <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
        <PrimaryButton disabled={!canSubmit} onClick={handleSubmit}>
          {submitting ? '제출 중...' : '발송대행 신청'}
        </PrimaryButton>
      </div>
    </div>
  );
}
